/**
 * Edit User Profile Form Component
 * Form for editing user profile information (name, bio, avatar).
 */

import React, { useRef, useState } from 'react';
import { fileToAvatarDataUrl } from '../../utils/fileToAvatarDataUrl';

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10MB source cap (it gets downscaled anyway)

const EditUserProfileForm = ({
  editForm,
  onFormChange,
  onSave,
  onCancel,
  saving,
  status
}) => {
  const fileInputRef = useRef(null);
  const [photoError, setPhotoError] = useState('');

  const handleChange = (field, value) => {
    onFormChange({ ...editForm, [field]: value });
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setPhotoError('');
    if (file.size > MAX_UPLOAD_BYTES) {
      setPhotoError('That image is too large (max 10MB).');
      return;
    }
    try {
      const dataUrl = await fileToAvatarDataUrl(file);
      handleChange('avatar', dataUrl);
    } catch (err) {
      setPhotoError(err.message || 'Could not process that image.');
    }
  };

  const bio = editForm.bio || '';

  return (
    <div className="edit-profile-section">
      {status && (
        <div className={`save-status ${status.type}`}>{status.text}</div>
      )}
      <div className="form-group">
        <label>Full Name</label>
        <input
          type="text"
          value={editForm.fullName}
          onChange={(e) => handleChange('fullName', e.target.value)}
          placeholder="Your full name"
        />
      </div>
      <div className="form-group">
        <label>Bio</label>
        <textarea
          value={bio}
          onChange={(e) => handleChange('bio', e.target.value)}
          placeholder="Tell us about yourself"
          rows={4}
          maxLength={500}
        />
        <span className="char-counter">{bio.length}/500</span>
      </div>
      <div className="form-group">
        <label>Profile Photo</label>
        <div className="avatar-upload">
          {editForm.avatar ? (
            <img className="avatar-preview" src={editForm.avatar} alt="Profile preview" />
          ) : (
            <div className="avatar-preview avatar-preview-placeholder">
              {(editForm.fullName || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="avatar-upload-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              style={{ display: 'none' }}
            />
            <button type="button" className="btn-upload" onClick={() => fileInputRef.current?.click()}>
              {editForm.avatar ? 'Change photo' : 'Upload photo'}
            </button>
            {editForm.avatar && (
              <button type="button" className="btn-remove-photo" onClick={() => handleChange('avatar', '')}>
                Remove
              </button>
            )}
          </div>
        </div>
        {photoError && <span className="photo-error">{photoError}</span>}
      </div>
      <div className="edit-profile-buttons">
        <button className="btn-save" onClick={onSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button className="btn-cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EditUserProfileForm;
